import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubmissions } from '@/hooks/useSubmissions';
import type { EPIRequest, Incident, Denunciation } from '@/types';

type TabType = 'epi' | 'incidentes' | 'denuncias';

const tabs: { key: TabType; label: string; icon: string }[] = [
  { key: 'epi', label: 'Solicitações de EPI', icon: 'ri-tools-line' },
  { key: 'incidentes', label: 'Incidentes', icon: 'ri-alarm-warning-line' },
  { key: 'denuncias', label: 'Denúncias', icon: 'ri-shield-user-line' },
];

const statusColors: Record<string, string> = {
  Pendente: 'bg-amber-50 text-amber-700',
  Aprovado: 'bg-emerald-50 text-emerald-700',
  Negado: 'bg-red-50 text-red-700',
  'Em análise': 'bg-blue-50 text-blue-700',
  'Concluído': 'bg-emerald-50 text-emerald-700',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function EPIDetail({ item }: { item: EPIRequest }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden transition-all">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-portal-gray-50/50 transition-colors cursor-pointer"
      >
        <div className="w-10 h-10 rounded-lg bg-portal-purple-50 flex items-center justify-center flex-shrink-0">
          <i className="ri-tools-line text-portal-purple text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-mono text-portal-gray-400">{item.displayId || item.id}</p>
          <p className="text-sm font-medium text-portal-gray-800 truncate">
            {item.itens.map((i) => `${i.tipo} (${i.q01 + i.q02 + i.qCaixa})`).join(', ')}
          </p>
          <p className="text-xs text-portal-gray-400 mt-0.5">{formatDate(item.createdAt)}</p>
        </div>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${statusColors[item.status]}`}>
          {item.status}
        </span>
        {open ?(
          <i className="ri-arrow-up-s-line text-portal-gray-400 text-sm flex-shrink-0"></i>
        ) : (
          <i className="ri-arrow-down-s-line text-portal-gray-400 text-sm flex-shrink-0"></i>
        )}
      </button>
      {open && (
        <div className="px-5 pb-4 border-t border-portal-gray-100 pt-4 space-y-3 bg-portal-gray-50/30">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-portal-gray-400 mb-0.5">Setor</p>
              <p className="text-sm text-portal-gray-700">{item.setor}</p>
            </div>
            <div>
              <p className="text-xs text-portal-gray-400 mb-0.5">Motivo</p>
              <p className="text-sm text-portal-gray-700">{item.motivo}</p>
            </div>
            <div>
              <p className="text-xs text-portal-gray-400 mb-0.5">Urgência</p>
              <p className="text-sm text-portal-gray-700">{item.urgencia}</p>
            </div>
          </div>
          {item.observacao && (
            <div>
              <p className="text-xs text-portal-gray-400 mb-0.5">Observação</p>
              <p className="text-sm text-portal-gray-700">{item.observacao}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-portal-gray-400 mb-1.5">Itens Solicitados</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-portal-gray-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-portal-gray-100">
                    <th className="text-left text-xs font-medium text-portal-gray-600 px-3 py-2">Tipo</th>
                    <th className="text-center text-xs font-medium text-portal-gray-600 px-3 py-2">01</th>
                    <th className="text-center text-xs font-medium text-portal-gray-600 px-3 py-2">02</th>
                    <th className="text-center text-xs font-medium text-portal-gray-600 px-3 py-2">Caixa</th>
                  </tr>
                </thead>
                <tbody>
                  {item.itens.filter((i) => (i.q01 + i.q02 + i.qCaixa) > 0).map((i, idx) => (
                    <tr key={idx} className="border-t border-portal-gray-100">
                      <td className="px-3 py-2 text-portal-gray-700">{i.tipo}</td>
                      <td className="px-3 py-2 text-center text-portal-gray-600">{i.q01 || '-'}</td>
                      <td className="px-3 py-2 text-center text-portal-gray-600">{i.q02 || '-'}</td>
                      <td className="px-3 py-2 text-center text-portal-gray-600">{i.qCaixa || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IncidentDetail({ item }: { item: Incident }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden transition-all">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-portal-gray-50/50 transition-colors cursor-pointer"
      >
        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
          <i className="ri-alarm-warning-line text-amber-500 text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-mono text-portal-gray-400">{item.displayId || item.id}</p>
          <p className="text-sm font-medium text-portal-gray-800 truncate">{item.tipoIncidente}</p>
          <p className="text-xs text-portal-gray-400 mt-0.5">{formatDate(item.createdAt)}</p>
        </div>
        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 flex-shrink-0">
          Registrado
        </span>
        {open ?(
          <i className="ri-arrow-up-s-line text-portal-gray-400 text-sm flex-shrink-0"></i>
        ) : (
          <i className="ri-arrow-down-s-line text-portal-gray-400 text-sm flex-shrink-0"></i>
        )}
      </button>
      {open && (
        <div className="px-5 pb-4 border-t border-portal-gray-100 pt-4 space-y-3 bg-portal-gray-50/30">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-portal-gray-400 mb-0.5">Setor</p>
              <p className="text-sm text-portal-gray-700">{item.setor}</p>
            </div>
            <div>
              <p className="text-xs text-portal-gray-400 mb-0.5">Ocorrência no setor?</p>
              <p className="text-sm text-portal-gray-700">{item.noSetor}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-portal-gray-400 mb-0.5">Descrição do Incidente</p>
            <p className="text-sm text-portal-gray-700 bg-white rounded-lg p-3 border border-portal-gray-100">{item.descricao}</p>
          </div>
          <div>
            <p className="text-xs text-portal-gray-400 mb-0.5">Declaração</p>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${item.declaracaoVerdade ?'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {item.declaracaoVerdade ?'Declaro que as informações são verdadeiras' : 'Não declarado'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function DenunciationDetail({ item }: { item: Denunciation }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden transition-all">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-portal-gray-50/50 transition-colors cursor-pointer"
      >
        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
          <i className="ri-shield-user-line text-emerald-600 text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-mono text-portal-gray-400">{item.displayId || item.id}</p>
          <p className="text-sm font-medium text-portal-gray-800 truncate">
            {item.tipoAssedio.length > 60 ?item.tipoAssedio.substring(0, 60) + '...' : item.tipoAssedio}
          </p>
          <p className="text-xs text-portal-gray-400 mt-0.5">{formatDate(item.createdAt)}</p>
        </div>
        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 flex-shrink-0">
          {item.identificar ?'Identificada' : 'Anônima'}
        </span>
        {open ?(
          <i className="ri-arrow-up-s-line text-portal-gray-400 text-sm flex-shrink-0"></i>
        ) : (
          <i className="ri-arrow-down-s-line text-portal-gray-400 text-sm flex-shrink-0"></i>
        )}
      </button>
      {open && (
        <div className="px-5 pb-4 border-t border-portal-gray-100 pt-4 space-y-3 bg-portal-gray-50/30">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-portal-gray-400 mb-0.5">Setor</p>
              <p className="text-sm text-portal-gray-700">{item.setor}</p>
            </div>
            <div>
              <p className="text-xs text-portal-gray-400 mb-0.5">Autores</p>
              <p className="text-sm text-portal-gray-700">{item.autoresNome}</p>
            </div>
            <div>
              <p className="text-xs text-portal-gray-400 mb-0.5">Nível de Assédio</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-portal-gray-200 rounded-full overflow-hidden max-w-[120px]">
                  <div className="h-full bg-red-400 rounded-full" style={{ width: `${(item.nivelAssedio / 9) * 100}%` }}></div>
                </div>
                <span className="text-sm font-medium text-portal-gray-700">{item.nivelAssedio}/9</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-portal-gray-400 mb-0.5">Última Ocorrência</p>
              <p className="text-sm text-portal-gray-700">{new Date(item.ultimaOcorrencia + 'T00:00:00').toLocaleDateString('pt-BR')}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-portal-gray-400 mb-0.5">Detalhes</p>
            <p className="text-sm text-portal-gray-700 bg-white rounded-lg p-3 border border-portal-gray-100">{item.detalhes}</p>
          </div>
          {item.observacoes && (
            <div>
              <p className="text-xs text-portal-gray-400 mb-0.5">Observações</p>
              <p className="text-sm text-portal-gray-700">{item.observacoes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ColaboradorHistorico() {
  const { user } = useAuth();
  const { epiRequests, incidents, denunciations, loading, refresh } = useSubmissions();
  const [activeTab, setActiveTab] = useState<TabType>('epi');

  useEffect(() => {
    refresh();
  }, [refresh]);

  const meusEPIs = epiRequests.filter((r) => r.userId === user?.id);
  const meusIncidentes = incidents.filter((i) => i.userId === user?.id);
  const minhasDenuncias = denunciations.filter(
    (d) => d.userId === user?.id && d.identificar === true
  );

  const getCount = (tab: TabType) => {
    if (tab === 'epi') return meusEPIs.length;
    if (tab === 'incidentes') return meusIncidentes.length;
    return minhasDenuncias.length;
  };

  const getContent = () => {
    if (loading) {
      return (
        <div className="bg-white rounded-xl border border-portal-gray-200 p-10 text-center">
          <div className="w-8 h-8 border-2 border-portal-purple border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-portal-gray-500">Carregando histórico...</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'epi':
        return meusEPIs.length === 0 ?(
          <div className="bg-white rounded-xl border border-portal-gray-200 p-10 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-portal-purple-50 flex items-center justify-center">
              <i className="ri-tools-line text-portal-purple text-xl"></i>
            </div>
            <p className="text-sm font-medium text-portal-gray-500">Nenhuma solicitação de EPI</p>
            <p className="text-xs text-portal-gray-400 mt-1">Suas solicitações de EPI aparecerão aqui.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {meusEPIs.map((epi) => (
              <EPIDetail key={epi.id} item={epi} />
            ))}
          </div>
        );
      case 'incidentes':
        return meusIncidentes.length === 0 ?(
          <div className="bg-white rounded-xl border border-portal-gray-200 p-10 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-amber-50 flex items-center justify-center">
              <i className="ri-alarm-warning-line text-amber-500 text-xl"></i>
            </div>
            <p className="text-sm font-medium text-portal-gray-500">Nenhum incidente registrado</p>
            <p className="text-xs text-portal-gray-400 mt-1">Seus registros de incidente aparecerão aqui.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {meusIncidentes.map((inc) => (
              <IncidentDetail key={inc.id} item={inc} />
            ))}
          </div>
        );
      case 'denuncias':
        return minhasDenuncias.length === 0 ?(
          <div className="bg-white rounded-xl border border-portal-gray-200 p-10 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-emerald-50 flex items-center justify-center">
              <i className="ri-shield-user-line text-emerald-600 text-xl"></i>
            </div>
            <p className="text-sm font-medium text-portal-gray-500">Nenhuma denúncia identificada</p>
            <p className="text-xs text-portal-gray-400 mt-1">Denúncias anônimas não aparecem aqui por questões de segurança.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {minhasDenuncias.map((den) => (
              <DenunciationDetail key={den.id} item={den} />
            ))}
          </div>
        );
    }
  };

  return (
    <div>
      <h1 className="text-xl font-bold text-portal-gray-900 mb-1">Meu Histórico</h1>
      <p className="text-sm text-portal-gray-500 mb-6">
        Visualize todas as suas solicitações e registros anteriores.
      </p>

      <div className="flex items-center gap-1 bg-portal-gray-100 rounded-xl p-1 mb-6 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === tab.key
                ?'bg-white text-portal-gray-800'
                : 'text-portal-gray-500 hover:text-portal-gray-700'
            }`}
          >
            <i className={`${tab.icon} text-sm`}></i>
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              activeTab === tab.key ?'bg-portal-purple-50 text-portal-purple' : 'bg-portal-gray-200 text-portal-gray-500'
            }`}>
              {getCount(tab.key)}
            </span>
          </button>
        ))}
      </div>

      {getContent()}
    </div>
  );
}
